import React from "react";
import { Star, Check, Sparkles, Lock, Radio, PlayCircle, Calendar, Disc3, Megaphone, Ticket } from "lucide-react";
import { useApp } from "../context/AppContext";

const TYPE_META = {
  show: { icon: Calendar, label: "Live Show", color: "#f59e0b" },
  release: { icon: Disc3, label: "Release", color: "#8b5cf6" },
  news: { icon: Megaphone, label: "News", color: "#38bdf8" },
};

function Bulletin({ events, onBuy }) {
  if (!events || events.length === 0) return null;
  return (
    <div className="mb-4">
      <p className="text-[11px] uppercase tracking-wide text-white/40 mb-2">Bulletin board</p>
      <div className="space-y-2">
        {events.map((e) => {
          const m = TYPE_META[e.type] || TYPE_META.news;
          const Icon = m.icon;
          const hasTicket = e.type === "show" && (e.ticketUrl || e.ticketPrice != null);
          return (
            <div key={e.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
              <div className="flex items-start gap-2.5">
                <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${m.color}22` }}>
                  <Icon size={15} style={{ color: m.color }} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full" style={{ background: `${m.color}22`, color: m.color }}>{m.label}</span>
                    {e.date && <span className="text-[11px] text-white/40">{e.date}</span>}
                  </div>
                  <p className="text-[14px] font-semibold text-white mt-1">{e.title}</p>
                  {e.location && <p className="text-[12px] text-white/45">{e.location}</p>}
                  {e.description && <p className="text-[12px] text-white/55 mt-1 leading-relaxed">{e.description}</p>}
                  {hasTicket && (
                    <button onClick={() => onBuy(e)} className="mt-2 inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white text-black text-[12px] font-semibold hover:bg-white/85">
                      <Ticket size={14} /> Buy Tickets{e.ticketPrice != null ? ` · $${Number(e.ticketPrice).toFixed(2)}` : ""}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ExclusiveSection() {
  const { artist, subscribed, subscriptionDetail, requestMembership, user, exclusive, exclusiveLocked, setAuthOpen, setActiveVideo, requestTicket, membershipPrice } = useApp();
  const m = artist.membership;
  const unlocked = !!(user && exclusive && !exclusiveLocked);

  return (
    <section className="px-5 pt-8">
      <div className="flex items-center gap-2 mb-4">
        <span className="section-dot bg-amber-400" />
        <h2 className="text-[13px] font-semibold tracking-[0.16em] uppercase text-white">Exclusive</h2>
        <span className="text-[10px] tracking-[0.14em] uppercase text-white/35">Members only</span>
      </div>

      {unlocked ? (
        <>
          <Bulletin events={exclusive.bulletins} onBuy={requestTicket} />
          <div className="rounded-2xl overflow-hidden border border-[#8b5cf6]/30 bg-[#8b5cf6]/[0.06]">
            <div className="px-4 pt-4 pb-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="flex items-center gap-1 text-[10px] tracking-[0.16em] uppercase text-red-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> {exclusive.live.status}
                </span>
              </div>
              <p className="text-[15px] font-semibold text-white">{exclusive.live.title}</p>
            </div>
            <button onClick={() => setActiveVideo({ title: exclusive.live.title, youtubeId: exclusive.live.youtubeId })} className="group relative w-full aspect-video">
              <div className="absolute inset-0 bg-gradient-to-br from-[#1a1430] to-[#0d0d18] flex items-center justify-center"><Radio size={40} className="text-[#8b5cf6]/40" /></div>
              <span className="absolute inset-0 flex items-center justify-center"><PlayCircle size={48} className="text-white drop-shadow group-hover:scale-105 transition-transform" /></span>
            </button>
            <div className="px-4 py-3 grid grid-cols-2 gap-2">
              {exclusive.drops.map((d) => (
                <div key={d.id} className="rounded-xl bg-white/[0.04] border border-white/10 p-3"><p className="text-[13px] font-medium text-white">{d.title}</p><p className="text-[11px] text-white/45 mt-0.5">{d.note}</p></div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#161616] to-[#0d0d0e] p-5">
          <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-amber-400/10 blur-2xl" />
          <div className="relative">
            <span className="w-10 h-10 rounded-full bg-[#8b5cf6]/15 flex items-center justify-center mb-3"><Lock size={18} className="text-[#a98bff]" /></span>
            <span className="flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-amber-300/90 mb-1"><Star size={12} fill="currentColor" /> Membership</span>
            <h3 className="font-cond font-bold text-[22px] uppercase tracking-wide text-white">{m.title}</h3>
            <p className="text-[13px] text-white/55 mt-1.5 leading-relaxed">{m.description || "Unlock the live stream, bulletin board, show tickets, early drops and members-only content."}</p>
            <ul className="mt-4 space-y-2">
              {(m.perks && m.perks.length ? m.perks : ["Live streams", "Show dates & ticket access", "Early drops", "Behind the scenes"]).map((p, i) => (
                <li key={`${i}-${p}`} className="flex items-center gap-2 text-[13px] text-white/70"><Check size={14} className="text-[#8b5cf6] shrink-0" /> {p}</li>
              ))}
            </ul>
            <div className="mt-5 flex items-end gap-1">
              <span className="font-display text-[30px] text-white">${Number(membershipPrice).toFixed(2)}</span>
              <span className="text-[12px] text-white/45 mb-1.5">/ 30 days</span>
            </div>
            {!user ? (
              <button onClick={() => setAuthOpen(true)} className="mt-4 w-full h-12 rounded-full bg-white text-black text-[14px] font-semibold flex items-center justify-center gap-2 hover:bg-white/85">
                <Sparkles size={16} /> Sign in to subscribe
              </button>
            ) : (
              <button onClick={requestMembership} disabled={subscribed} className={`mt-4 w-full h-12 rounded-full text-[14px] font-semibold flex items-center justify-center gap-2 ${subscribed ? "bg-[#8b5cf6] text-white" : "bg-white text-black hover:bg-white/85"}`}>
                {subscribed ? (<><Check size={16} /> Member — {subscriptionDetail?.days_left > 0 ? `${subscriptionDetail.days_left} days left` : "Active"}</>) : (<><Sparkles size={16} /> Subscribe · ${Number(membershipPrice).toFixed(2)}/30 days</>)}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
